import type { ReactNode } from "react";
import { useI18n } from "../../../lib/useI18n.js";

/** Hand-drawn checkbox used across the card. */
export function Check({ checked, onChange, children }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <label className={"check" + (checked ? " is-on" : "")}>
      <span className="box" aria-hidden="true">
        <svg viewBox="0 0 16 16" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5 L6.5 12 L13 4.5" />
        </svg>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: "none" }}
      />
      {children != null && <span>{children}</span>}
    </label>
  );
}

/** Small bordered − value + stepper (repeat "every", custom reminder amount). */
export function MiniStepper({ value, onStep }: { value: ReactNode; onStep: (delta: number) => void }) {
  const { t } = useI18n();
  return (
    <div className="mini-stepper">
      <button type="button" onClick={() => onStep(-1)} aria-label={t("common.decrease")}>−</button>
      <span className="val">{value}</span>
      <button type="button" onClick={() => onStep(+1)} aria-label={t("common.increase")}>+</button>
    </div>
  );
}

/** "45 min" / "1h 30m" rendering for the duration stepper. */
export function fmtMinutes(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

/** Duration stepper (15-minute steps). */
export function DurationStepper({ minutes, onChange }: { minutes: number; onChange: (m: number) => void }) {
  const { t } = useI18n();
  const step = (delta: number) => onChange(Math.max(15, minutes + delta));
  return (
    <div className="duration">
      <div className="stepper">
        <button type="button" onClick={() => step(-15)} aria-label={t("common.decrease")}>−</button>
        <span className="val">{fmtMinutes(minutes)}</span>
        <button type="button" onClick={() => step(+15)} aria-label={t("common.increase")}>+</button>
      </div>
    </div>
  );
}
